import React, { useMemo, useState } from "react";
import CallerBreakdownCharts from "./CallerBreakdownCharts";

const TeamUsageMonthlyTable = ({ data, isLoading, budgetConfig }) => {
  const [sortKey, setSortKey] = useState("total_cost_usd");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedCaller, setExpandedCaller] = useState(null);

  const callers = useMemo(() => {
    if (!data?.callers) return [];
    return [...data.callers].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortDir === "asc"
        ? String(aVal || "").localeCompare(String(bVal || ""))
        : String(bVal || "").localeCompare(String(aVal || ""));
    });
  }, [data, sortKey, sortDir]);

  const limitMap = useMemo(() => {
    if (!budgetConfig?.caller_limits) return {};
    const m = {};
    for (const cl of budgetConfig.caller_limits) {
      if (cl.caller && cl.monthly_limit_usd != null) {
        m[cl.caller] = cl.monthly_limit_usd;
      }
    }
    return m;
  }, [budgetConfig]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortKey !== field) return <span className='text-text-tertiary/40 ml-0.5'>↕</span>;
    return <span className='text-accent-purple ml-0.5'>{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  if (isLoading) {
    return (
      <div className='py-12 flex items-center justify-center text-text-tertiary'>
        Loading...
      </div>
    );
  }

  if (!callers.length) {
    return (
      <div className='py-12 text-center text-text-tertiary'>
        No usage data available for this period.
      </div>
    );
  }

  return (
    <div className='overflow-x-auto'>
      <table className='w-full'>
        <thead>
          <tr className='border-b border-border-subtle'>
            <th
              className='text-left px-4 py-3 text-body-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:text-text-primary'
              onClick={() => handleSort("caller")}
            >
              Person <SortIcon field='caller' />
            </th>
            <th
              className='text-right px-4 py-3 text-body-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:text-text-primary'
              onClick={() => handleSort("total_cost_usd")}
            >
              Total Cost <SortIcon field='total_cost_usd' />
            </th>
            <th
              className='text-right px-4 py-3 text-body-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:text-text-primary'
              onClick={() => handleSort("call_count")}
            >
              Calls <SortIcon field='call_count' />
            </th>
            <th className='text-left px-4 py-3 text-body-xs font-medium text-text-secondary uppercase tracking-wider'>
              Budget Usage
            </th>
          </tr>
        </thead>
        <tbody className='divide-y divide-border-subtle'>
          {callers.map((c) => {
            const limit = limitMap[c.caller];
            const pct = limit ? (c.total_cost_usd / limit) * 100 : null;
            const isOver = pct != null && pct >= 100;
            const isWarning = pct != null && pct >= 80 && pct < 100;

            const isExpanded = expandedCaller === c.caller;
            const hasBreakdown = c.call_count > 0;

            return (
              <React.Fragment key={c.caller}>
                <tr
                  className={`transition-colors ${hasBreakdown ? "cursor-pointer hover:bg-surface-neutral/50" : ""} ${isExpanded ? "bg-surface-neutral/30" : ""}`}
                  onClick={() => hasBreakdown && setExpandedCaller(isExpanded ? null : c.caller)}
                >
                  <td className='px-4 py-3'>
                    <div className='flex items-center gap-2'>
                      {hasBreakdown && (
                        <svg
                          className={`w-3.5 h-3.5 text-text-tertiary transition-transform ${isExpanded ? "rotate-90" : ""}`}
                          fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}
                        >
                          <path strokeLinecap='round' strokeLinejoin='round' d='M9 5l7 7-7 7' />
                        </svg>
                      )}
                      <span className='text-body-sm font-medium text-text-primary'>{c.caller}</span>
                    </div>
                  </td>
                  <td className='px-4 py-3 text-right'>
                    <span className='text-body-sm font-semibold text-text-primary tabular-nums'>
                      ${c.total_cost_usd.toFixed(2)}
                    </span>
                  </td>
                  <td className='px-4 py-3 text-right'>
                    <span className='text-body-sm text-text-secondary tabular-nums'>
                      {c.call_count.toLocaleString()}
                    </span>
                  </td>
                  <td className='px-4 py-3'>
                    {limit != null ? (
                      <div className='flex items-center gap-3 min-w-[200px]'>
                        <div className='flex-1 h-2 bg-surface-neutral rounded-full overflow-hidden'>
                          <div
                            className={`h-full rounded-full transition-all ${
                              isOver ? "bg-red-500" : isWarning ? "bg-amber-500" : "bg-accent-purple"
                            }`}
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                        <span className={`text-body-xs font-medium tabular-nums min-w-[80px] text-right ${
                          isOver ? "text-red-600" : isWarning ? "text-amber-600" : "text-text-secondary"
                        }`}>
                          ${c.total_cost_usd.toFixed(2)} / ${limit.toFixed(0)}
                        </span>
                      </div>
                    ) : (
                      <span className='text-body-xs text-text-tertiary'>No limit</span>
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr className='bg-surface-neutral/20'>
                    <td colSpan={4}>
                      <CallerBreakdownCharts caller={c} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className='border-t-2 border-border-default bg-surface-neutral/30'>
            <td className='px-4 py-3'>
              <span className='text-body-sm font-semibold text-text-primary'>Team Total</span>
            </td>
            <td className='px-4 py-3 text-right'>
              <span className='text-body-sm font-bold text-accent-purple tabular-nums'>
                ${data?.team_total_cost_usd?.toFixed(2) ?? "0.00"}
              </span>
            </td>
            <td className='px-4 py-3 text-right'>
              <span className='text-body-sm font-medium text-text-secondary tabular-nums'>
                {callers.reduce((s, c) => s + c.call_count, 0).toLocaleString()}
              </span>
            </td>
            <td className='px-4 py-3'>
              {budgetConfig?.env_monthly_limit_usd != null && (
                <span className='text-body-xs text-text-secondary'>
                  Env limit: ${budgetConfig.env_monthly_limit_usd.toFixed(0)}
                </span>
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default TeamUsageMonthlyTable;
